import { catalogIdsOf, modeTakesPastedLinks, type PlayableRequest, type PlayModeConfig, type PlayerProvider } from '../services/playModes';
import type { CatalogIds } from './songIdentity';
import { canonicalTrackUrl, notTrackMessage, parseMusicLink, platformNameOf } from './songLinks';

export interface PlayerLink {
  url: string;
  isDirect: boolean;
}

export const LISTEN_PROVIDERS: PlayerProvider[] = ['spotify', 'apple', 'tidal'];

export function playerSearchUrl(title: string, artist: string, provider: PlayerProvider): string {
  const query = encodeURIComponent(`${title} ${artist}`);
  if (provider === 'spotify') return `https://open.spotify.com/search/${query}`;
  if (provider === 'tidal') return `https://tidal.com/search?q=${query}`;
  return `https://music.apple.com/us/search?term=${query}`;
}

export function playerLinkFromIds(
  song: { title: string; artist: string },
  trackIds: CatalogIds,
  provider: PlayerProvider,
): PlayerLink {
  const trackId = trackIds[provider];
  if (trackId) return { url: canonicalTrackUrl(provider, trackId), isDirect: true };
  return { url: playerSearchUrl(song.title, song.artist, provider), isDirect: false };
}

export function playerLinkFor(request: PlayableRequest, provider: PlayerProvider): PlayerLink {
  return playerLinkFromIds(request.song, catalogIdsOf(request), provider);
}

export interface PreviewTrack {
  requestId: string;
  title: string;
  artist: string;
  trackIds: CatalogIds;
  eventProvider: PlayerProvider;
  acceptsLink: boolean;
}

export function buildPreviewTrack(request: PlayableRequest & { id: string }, mode: PlayModeConfig): PreviewTrack {
  const catalogIds = catalogIdsOf(request);
  const trackIds: CatalogIds = {};
  for (const provider of LISTEN_PROVIDERS) {
    const trackId = catalogIds[provider];
    if (trackId) trackIds[provider] = trackId;
  }
  return {
    requestId: request.id,
    title: request.song.title,
    artist: request.song.artist,
    trackIds,
    eventProvider: mode.provider,
    acceptsLink: modeTakesPastedLinks(mode),
  };
}

export function previewPasteProblem(raw: string, eventProvider: PlayerProvider): string | null {
  const name = platformNameOf(eventProvider);
  const trimmed = raw.trim();
  if (!trimmed) return `Paste a ${name} track link`;
  if (!trimmed.startsWith('https://')) return `Paste a full https:// ${name} link`;
  const parsed = parseMusicLink(trimmed);
  if (parsed.kind === 'not_track') return notTrackMessage(parsed.provider, parsed.what);
  return null;
}
